/**
 * Cascade Repair Module - Phase 2.1
 * Last-Minute Insertion Implementation
 *
 * Implements recursive cascade repair with rollback for moving aircraft
 * to make space for last-minute insertions.
 *
 * Key features:
 * - Recursive cascade handling (A→B→C chains)
 * - Total move counting (all moves in chain)
 * - Rollback on failure (restore original state)
 * - Move budget enforcement (user-configurable limit)
 */

import { ParkingRequest, Spot, Placement } from "./types";
import { setRange } from "./bitmap";

// ==== Helper Functions ====
// All functions use the convention: [startBucket, endBucket) - endBucket is EXCLUSIVE

/**
 * Transaction log entry for rollback
 * Records a single bitmap modification for efficient undo
 */
interface BitmapTransaction {
  spot: Spot;
  startBucket: number;
  endBucket: number;
  operation: 'mark' | 'clear';
}

/**
 * Transaction log for rollback
 * More memory efficient than copying entire BigUint64Arrays
 */
class TransactionLog {
  private transactions: BitmapTransaction[] = [];

  record(spot: Spot, startBucket: number, endBucket: number, operation: 'mark' | 'clear'): void {
    this.transactions.push({ spot, startBucket, endBucket, operation });
  }

  rollback(): void {
    // Undo in reverse order (LIFO)
    for (let i = this.transactions.length - 1; i >= 0; i--) {
      const tx = this.transactions[i];
      if (tx.operation === 'mark') {
        // Undo mark = clear
        clearOccupancyInternal(tx.spot, tx.startBucket, tx.endBucket);
      } else {
        // Undo clear = mark
        markOccupiedInternal(tx.spot, tx.startBucket, tx.endBucket);
      }
    }
    this.transactions = [];
  }

  clear(): void {
    this.transactions = [];
  }
}

/**
 * Internal: Mark spot occupied without validation (for rollback)
 */
function markOccupiedInternal(spot: Spot, startBucket: number, endBucket: number): void {
  setRange(spot.occupancy, startBucket, endBucket - 1); // setRange is inclusive, so -1
}

/**
 * Internal: Clear occupancy without validation (for rollback)
 */
function clearOccupancyInternal(spot: Spot, startBucket: number, endBucket: number): void {
  for (let bucket = startBucket; bucket < endBucket; bucket++) {
    const wordIndex = Math.floor(bucket / 64);
    const bitIndex = BigInt(bucket % 64);
    spot.occupancy[wordIndex] &= ~(1n << bitIndex);
  }
}

/**
 * Mark a spot as occupied for a time range (exported for testing)
 * @param spot Spot to mark
 * @param startBucket Start bucket (inclusive)
 * @param endBucket End bucket (EXCLUSIVE)
 * @throws Error if endBucket <= startBucket (validation)
 */
export function markOccupied(spot: Spot, startBucket: number, endBucket: number): void {
  if (endBucket <= startBucket) {
    throw new Error(`Invalid range: endBucket (${endBucket}) must be > startBucket (${startBucket})`);
  }
  markOccupiedInternal(spot, startBucket, endBucket);
}

/**
 * Clear occupancy from a spot for a time range
 * @param spot Spot to clear
 * @param startBucket Start bucket (inclusive)
 * @param endBucket End bucket (EXCLUSIVE)
 * @throws Error if endBucket <= startBucket (validation)
 */
function clearOccupancy(spot: Spot, startBucket: number, endBucket: number): void {
  if (endBucket <= startBucket) {
    throw new Error(`Invalid range: endBucket (${endBucket}) must be > startBucket (${startBucket})`);
  }
  clearOccupancyInternal(spot, startBucket, endBucket);
}

/**
 * Check if a spot is available for a time range (exported for testing)
 * @param spot Spot to check
 * @param startBucket Start bucket (inclusive)
 * @param endBucket End bucket (EXCLUSIVE)
 * @returns True if spot is free for entire range
 * @throws Error if endBucket <= startBucket (validation)
 */
export function isAvailable(spot: Spot, startBucket: number, endBucket: number): boolean {
  if (endBucket <= startBucket) {
    throw new Error(`Invalid range: endBucket (${endBucket}) must be > startBucket (${startBucket})`);
  }
  for (let bucket = startBucket; bucket < endBucket; bucket++) {
    const wordIndex = Math.floor(bucket / 64);
    const bitIndex = BigInt(bucket % 64);
    if ((spot.occupancy[wordIndex] & (1n << bitIndex)) !== 0n) {
      return false; // Occupied
    }
  }
  return true; // Free
}

// ==== Main Types & Functions ====

/**
 * Represents a single aircraft move in a cascade chain
 */
export interface CascadeMove {
  aircraft: ParkingRequest;
  fromSpot: string;
  toSpot: string;
}

/**
 * Result of a cascade repair attempt
 */
export interface RepairResult {
  success: boolean;
  moves: CascadeMove[];
  targetPlacement?: {
    spotId: string;
    orientationId: string;
  } | null;
}

/**
 * Configuration options for cascade repair
 */
export interface RepairOptions {
  /** Maximum number of moves allowed in repair (default: 3) */
  maxRepairMoves?: number;

  /** Maximum cascade depth (default: 3) */
  maxDepth?: number;
}

/**
 * Try to repair placement by cascading moves
 *
 * This function attempts to move one aircraft (mover) to make space for
 * a target aircraft. If the mover's desired spot is occupied, it recursively
 * moves that occupant, creating a cascade chain.
 *
 * @param mover Aircraft to move (currently blocking target)
 * @param target New aircraft trying to fit (ultimate goal)
 * @param placement Current placement mapping (mutated during repair)
 * @param spots All available spots on ramp
 * @param allAircraft All aircraft in schedule
 * @param options Repair configuration
 * @param moveChain Moves made so far in cascade
 * @param depth Current recursion depth
 * @returns RepairResult with success status and move chain
 */
export function tryCascadeRepair(
  mover: ParkingRequest,
  target: ParkingRequest,
  placement: Placement,
  spots: Spot[],
  allAircraft: ParkingRequest[],
  options: RepairOptions,
  moveChain: CascadeMove[],
  depth: number
): RepairResult {
  const { maxRepairMoves = 3, maxDepth = 3 } = options;

  // Enforce move budget
  if (moveChain.length >= maxRepairMoves) {
    return { success: false, moves: [] };
  }

  // Enforce depth limit
  if (depth >= maxDepth) {
    return { success: false, moves: [] };
  }

  // Performance optimization: Create spot lookup map (O(1) instead of O(n))
  const spotMap = new Map(spots.map((s) => [s.id, s]));

  // Get mover's current spot
  const moverPlacement = placement[mover.id];
  if (!moverPlacement || !("spotId" in moverPlacement)) {
    return { success: false, moves: [] };
  }
  const originalSpotId = moverPlacement.spotId;
  const originalOrientationId = moverPlacement.orientationId;

  // Find mover's current spot object
  const originalSpot = spotMap.get(originalSpotId);
  if (!originalSpot) {
    return { success: false, moves: [] };
  }

  // Transaction log for rollback (more memory efficient than BigUint64Array copy)
  const txLog = new TransactionLog();

  // Remove mover from current spot
  clearOccupancy(originalSpot, mover.arrivalBucket, mover.departureBucket);
  txLog.record(originalSpot, mover.arrivalBucket, mover.departureBucket, 'clear');

  const originalPlacement = placement[mover.id];
  delete placement[mover.id];

  // Check if target can fit in mover's old spot now
  const targetCanFitInOriginal =
    originalSpot.allowedSizes.includes(target.sizeCategory) &&
    isAvailable(
      originalSpot,
      target.arrivalBucket,
      target.departureBucket
    );

  if (!targetCanFitInOriginal) {
    // Target still can't fit - rollback and fail
    txLog.rollback();
    placement[mover.id] = originalPlacement;
    return { success: false, moves: [] };
  }

  // Target CAN fit! Now find a SIZE-COMPATIBLE spot for mover (excluding original spot)
  // Strategy: Prefer free spots (no cascade), fallback to occupied spots (cascade required)
  let newSpot: Spot | null = null;
  let occupant: ParkingRequest | null = null;
  let occupiedCandidate: { spot: Spot; occupant: ParkingRequest } | null = null;

  // Single pass: Find best spot (free preferred, occupied as fallback)
  for (const spot of spots) {
    if (spot.id === originalSpotId) continue; // Skip original
    if (!spot.allowedSizes.includes(mover.sizeCategory)) continue; // Size check

    // Check if spot is free
    const isFree = isAvailable(spot, mover.arrivalBucket, mover.departureBucket);

    if (isFree) {
      // Found a free spot! Use it immediately
      newSpot = spot;
      occupant = null;
      break;
    } else if (!occupiedCandidate) {
      // Not free, but save as fallback candidate for cascading
      const potentialOccupant = findOccupant(spot.id, placement, allAircraft);
      if (potentialOccupant) {
        occupiedCandidate = { spot, occupant: potentialOccupant };
      }
    }
  }

  // If no free spot found, use occupied candidate (requires cascade)
  if (!newSpot && occupiedCandidate) {
    newSpot = occupiedCandidate.spot;
    occupant = occupiedCandidate.occupant;
  }

  if (!newSpot) {
    // Can't re-place mover anywhere - rollback and fail
    txLog.rollback();
    placement[mover.id] = originalPlacement;
    return { success: false, moves: [] };
  }

  if (occupant === null) {
    // FREE SPOT - success!
    // Place target in original spot
    markOccupied(originalSpot, target.arrivalBucket, target.departureBucket);
    txLog.record(originalSpot, target.arrivalBucket, target.departureBucket, 'mark');

    placement[target.id] = {
      spotId: originalSpotId,
      orientationId: originalOrientationId,
    };

    // Place mover in new spot
    markOccupied(newSpot, mover.arrivalBucket, mover.departureBucket);
    txLog.record(newSpot, mover.arrivalBucket, mover.departureBucket, 'mark');

    placement[mover.id] = {
      spotId: newSpot.id,
      orientationId: newSpot.orientations[0]?.id || "nose_in", // Use spot's first orientation
    };

    // Success! Clear transaction log (no rollback needed)
    txLog.clear();

    return {
      success: true,
      moves: [
        ...moveChain,
        {
          aircraft: mover,
          fromSpot: originalSpotId,
          toSpot: newSpot.id,
        },
      ],
      targetPlacement: {
        spotId: originalSpotId,
        orientationId: originalOrientationId,
      },
    };
  } else {
    // CASCADE NEEDED - recursively move occupant
    // First, tentatively place target
    markOccupied(originalSpot, target.arrivalBucket, target.departureBucket);
    txLog.record(originalSpot, target.arrivalBucket, target.departureBucket, 'mark');

    placement[target.id] = {
      spotId: originalSpotId,
      orientationId: originalOrientationId,
    };

    // Try to cascade (nested call will manage its own rollback if needed)
    const cascadeResult = tryCascadeRepair(
      occupant,
      target,
      placement,
      spots,
      allAircraft,
      options,
      [
        ...moveChain,
        {
          aircraft: mover,
          fromSpot: originalSpotId,
          toSpot: newSpot.id,
        },
      ],
      depth + 1
    );

    if (cascadeResult.success) {
      // Cascade succeeded! Place mover in new spot
      markOccupied(newSpot, mover.arrivalBucket, mover.departureBucket);
      txLog.clear(); // Success, no rollback needed
      placement[mover.id] = {
        spotId: newSpot.id,
        orientationId: newSpot.orientations[0]?.id || "nose_in", // Use spot's first orientation
      };
      return cascadeResult;
    } else {
      // Cascade failed - rollback all changes made at this level
      txLog.rollback();

      // Restore placement state
      delete placement[target.id];
      placement[mover.id] = originalPlacement;

      return { success: false, moves: [] };
    }
  }
}

/**
 * Find which aircraft is occupying a specific spot
 *
 * For multi-segment placements, returns aircraft if it occupies the spot
 * during ANY of its segments.
 *
 * @param spotId Spot ID to check
 * @param placement Current placement mapping
 * @param allAircraft All aircraft in schedule
 * @returns Aircraft occupying the spot, or null if free
 */
export function findOccupant(
  spotId: string,
  placement: Placement,
  allAircraft: ParkingRequest[]
): ParkingRequest | null {
  for (const aircraft of allAircraft) {
    const aircraftPlacement = placement[aircraft.id];
    if (!aircraftPlacement) {
      continue;
    }

    // Handle single-segment placements
    if ("spotId" in aircraftPlacement) {
      if (aircraftPlacement.spotId === spotId) {
        return aircraft;
      }
    } else {
      // Handle multi-segment placements - check ALL segments
      for (const segment of aircraftPlacement.segments) {
        if (segment.spotId === spotId) {
          return aircraft;
        }
      }
    }
  }

  return null;
}
